export interface CropRect {
  /** Top-left x in master, 0..1. */
  x: number;
  /** Top-left y in master, 0..1. */
  y: number;
  /** Crop width as fraction of master width, 0..1. */
  width: number;
  /** Crop height as fraction of master height, 0..1. */
  height: number;
}

export type ScenarioId =
  | "interview-segment"
  | "talk-from-lectern"
  | "prayer"
  | "baptism";

export type ControllableCam = "cam1" | "cam2" | "cam3";

export interface Preset {
  /** Stable identifier within a (scenario, cam). */
  id: string;
  /** Human-readable label shown in the panel. */
  label: string;
  cam: ControllableCam;
  scenarioId: ScenarioId;
  crop: CropRect;
}

export interface SceneMaster {
  url: string;
  width: number;
  height: number;
}

export interface Scenario {
  id: ScenarioId;
  label: string;
  master: SceneMaster;
  /** Per-cam preset library — display order is library order. */
  presets: Record<ControllableCam, Preset[]>;
  /** Preset id (per cam) the cam opens to when the scenario is selected. */
  defaults: Record<ControllableCam, string>;
}

export function lerpCrop(from: CropRect, to: CropRect, t: number): CropRect {
  const mix = (a: number, b: number) => a + (b - a) * t;
  return {
    x: mix(from.x, to.x),
    y: mix(from.y, to.y),
    width: mix(from.width, to.width),
    height: mix(from.height, to.height),
  };
}

export function clampCrop(crop: CropRect): CropRect {
  const width = Math.min(1, Math.max(0.12, crop.width));
  const height = Math.min(1, Math.max(0.12, crop.height));
  return {
    x: Math.min(1 - width, Math.max(0, crop.x)),
    y: Math.min(1 - height, Math.max(0, crop.y)),
    width,
    height,
  };
}

export function nudgeCrop(crop: CropRect, dx: number, dy: number): CropRect {
  return clampCrop({
    ...crop,
    x: crop.x + dx,
    y: crop.y + dy,
  });
}

export function zoomCrop(crop: CropRect, factor: number): CropRect {
  const cx = crop.x + crop.width / 2;
  const cy = crop.y + crop.height / 2;
  const width = crop.width * factor;
  const height = crop.height * factor;
  return clampCrop({
    x: cx - width / 2,
    y: cy - height / 2,
    width,
    height,
  });
}
